// Core imports
import { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Space, Col, Modal, Row, Typography, Divider, Card, Grid, Checkbox } from 'antd';
import BoxIcon from '../Icons/BoxIcon';

// Misc imports
import { INTEGRATION_CATEGORY, INTEGRATIONS } from '../../constants/integrations';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;
const { Meta } = Card;

const CreateIntegrationModal = ({
  isModalOpen,
  closeModal,
  setIntegration
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = (screens.xs || screens.sm || screens.md) && !screens.lg;
  const [integrationCategoryFilters, setIntegrationCategoryFilters] = useState([]);
  const [filteredIntegrationCategories, setFilteredIntegrationCategories] = useState(Object.values(INTEGRATION_CATEGORY));

  const handleCloseModal = () => {
    setIntegrationCategoryFilters([]);
    closeModal();
  };

  useEffect(() => {
    if (integrationCategoryFilters.length > 0) {
      const filtered = Object.values(INTEGRATION_CATEGORY).filter((category) => integrationCategoryFilters.includes(category));
      setFilteredIntegrationCategories(filtered);
    } else {
      setFilteredIntegrationCategories(Object.values(INTEGRATION_CATEGORY));
    }
  }, [integrationCategoryFilters]);

  const handleCheckboxChange = (checkedValues) => {
    setIntegrationCategoryFilters(checkedValues);
  };

  return (
    <Modal
      open={isModalOpen}
      className={`${isMobileSize ? 'w-80' : 'w-50'} ant-modal-p-0`}
      centered
      onCancel={handleCloseModal}
      destroyOnClose
      footer={null}
      forceRender
      title={
        <Fragment>
          <Paragraph className="mb-1">
            <Space size={8} className="align-items-center">
              <BoxIcon name="plug" />
              {t('Create New Integration')}
            </Space>
          </Paragraph>

          <Text className="text-muted">
            {t('Connect your established tools and streamline your workflows.')}
          </Text>
        </Fragment>
      }
    >
      <Row className={isMobileSize ? 'flex-column' : ''}>
        <Col span={isMobileSize ? 24 : 8} flex={1} className="overflow-y-auto overflow-x-hidden scrollbar-custom pb-2" style={{ height: isMobileSize ? 'auto' : '500px' }}>
          <Row className="px-6 py-3" justify="space-between" align="middle">
            <Col>
              <Text className="fs-xs" type="primary">
                {t('SCOPE FILTERS')}
              </Text>
            </Col>

            <Col>
              <Button className="fs-xs fw-400" type="link" onClick={() => handleCheckboxChange([])}>
                <Text className="fs-xs" type="secondary">{t('CLEAR FILTERS')}</Text>
              </Button>
            </Col>
          </Row>

          <Checkbox.Group value={integrationCategoryFilters} className="mb-0 pl-4" onChange={handleCheckboxChange}>
            <Row className="px-2">
              {
                Object.values(INTEGRATION_CATEGORY)
                  .filter(category => Object.values(INTEGRATIONS).some(integration => integration.category === category && !integration.hidden)) // filter out scopes that only contain hidden integrations
                  .map(category => {
                    return (
                      <Col span={24}>
                        <Checkbox value={category}>{t(category, { context: 'integration category' })}</Checkbox>
                      </Col>
                    );
                  })
              }
            </Row>
          </Checkbox.Group>
        </Col>

        <Divider type="vertical" className="h-auto m-0" />

        <Col
          span={isMobileSize ? 24 : 15}
          flex={4}
          className={classNames('py-6', {
            'px-6': isMobileSize,
            'pl-12': !isMobileSize
          })}
        >
          <Row className="mb-4" justify="space-between">
            <Col>
              <Text className="fs-md" type="primary">
                {t('PLATFORMS')}
              </Text>

              {
                filteredIntegrationCategories?.length === Object.values(INTEGRATION_CATEGORY).length
                  ? <Text className="fs-md ml-3" type="secondary">{t('All Scopes')} ({filteredIntegrationCategories.length})</Text>
                  : <Text ellipsis className="fs-md ml-3" type="secondary">{filteredIntegrationCategories.map((category) => t(category, { context: 'integration category' })).join(' / ')} ({filteredIntegrationCategories.length})</Text>
              }
            </Col>
          </Row>

          <Row gutter={[12, 12]} className="overflow-y-auto overflow-x-hidden scrollbar-custom py-2" style={{ minHeight: '300px', maxHeight: '400px' }}>
            {
              filteredIntegrationCategories.map((category) => {
                // Render all tools for the filtered scope
                return Object.values(INTEGRATIONS)
                  .filter((integration) => integration.category === category && !integration.hidden)
                  .map((integration) => (
                    <Col className={isMobileSize ? 'w-100' : 'w-50'} >
                      <Card
                        className="p-0 h-100"
                        cover={
                          <Row gutter={[12, 12]} align="middle" justify="center" className="mx-auto w-fc text-center mt-5">
                            <Col span={24}>
                              <Text className="fs-sm" strong>{t(category, { context: 'integration category' }).toUpperCase()}</Text>
                            </Col>
                            <Col span={24}>
                              {integration.renderTypeIcon({ className: 'fs-xxxl' })}
                            </Col>
                            <Col span={24}>
                              <Text className="fs-lg" strong>{t(integration.type, { context: 'type' })}</Text>
                            </Col>
                            <Col className="px-5 fs-sm" span={24}>
                              <Paragraph ellipsis={{ rows: 2, tooltip: t(integration.type, { context: 'type description' }) }}>{t(integration.type, { context: 'type description' })}</Paragraph>
                            </Col>
                          </Row>
                        }
                      >
                        <Meta
                          className="w-fc mx-auto"
                          description={
                            <Row align="middle" justify="center" className="mb-2">
                              <Col span={24}>
                                <Button onClick={() => setIntegration({ type: integration.type, category: category })}>
                                  {t('Integrate')}
                                </Button>
                              </Col>
                            </Row>
                          }
                        />
                      </Card>
                    </Col>
                  ));
              })
            }
          </Row>
        </Col>
      </Row>
    </Modal>
  );
};

CreateIntegrationModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  setIntegration: PropTypes.func
};
export default CreateIntegrationModal;
